/**
 * Splits an email plaintext body into the message itself and any tail of
 * quoted-reply lines. Gmail's `bodyPlain` preserves quoted history with
 * `> ` prefixes; we hide that behind a "Show quoted text" toggle in the UI
 * while keeping it accessible.
 *
 * Heuristic: walk lines from the bottom, accumulating any contiguous tail
 * block of lines that are either empty or start with `>` (one or more).
 * Then peel one or two trailing lead-in lines like "On <date>, <name> wrote:"
 * (Gmail-style) or "From: <addr>" (Outlook-style) into the quoted block too.
 *
 * Falls back to { visible: body, quoted: '', hasQuoted: false } when nothing
 * matches — keeps short replies and plain text intact.
 */
export interface SplitBody {
  visible: string;
  quoted: string;
  hasQuoted: boolean;
}

const LEAD_IN_PATTERNS = [
  /^on\s.+\s(wrote|said):?\s*$/i,
  /^_{2,}\s*$/,
  /^-{2,}\s+original message\s+-{2,}\s*$/i,
  /^from:\s/i,
];

export function splitQuotedBody(body: string | null | undefined): SplitBody {
  if (!body) return { visible: '', quoted: '', hasQuoted: false };
  const lines = body.replace(/\r\n/g, '\n').split('\n');

  // Walk backward, grabbing quoted-prefixed and blank lines.
  let i = lines.length - 1;
  while (i >= 0) {
    const line = lines[i] ?? '';
    if (line.length === 0 || /^>/.test(line.trim())) {
      i--;
      continue;
    }
    break;
  }
  let firstQuotedIdx = i + 1;

  // Peel a lead-in if present (max 2 lines above the quoted block).
  for (let peeled = 0; peeled < 2 && firstQuotedIdx > 0; peeled++) {
    const candidate = lines[firstQuotedIdx - 1] ?? '';
    if (LEAD_IN_PATTERNS.some((p) => p.test(candidate.trim()))) {
      firstQuotedIdx--;
    } else {
      break;
    }
  }

  if (firstQuotedIdx >= lines.length) {
    return { visible: body, quoted: '', hasQuoted: false };
  }

  const visibleLines = lines.slice(0, firstQuotedIdx);
  // Trim trailing blank lines on visible.
  while (visibleLines.length && visibleLines[visibleLines.length - 1] === '') {
    visibleLines.pop();
  }
  const quotedLines = lines.slice(firstQuotedIdx);
  // Trim leading blanks from quoted.
  while (quotedLines.length && quotedLines[0] === '') {
    quotedLines.shift();
  }
  // Strip the leading `> ` from each quoted line for cleaner rendering.
  const cleanedQuoted = quotedLines.map((l) => l.replace(/^>+\s?/, '')).join('\n');

  return {
    visible: visibleLines.join('\n'),
    quoted: cleanedQuoted,
    hasQuoted: cleanedQuoted.trim().length > 0,
  };
}
